import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { bautizosAPI } from "@/lib/api";
import { ArrowLeft, Save, Droplets } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function BautizoFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    numero_libro: "",
    numero_folio: "",
    numero_partida: "",
    fecha_bautizo: "",
    lugar_bautizo: "",
    bautizado_nombre: "",
    bautizado_apellido: "",
    bautizado_fecha_nacimiento: "",
    bautizado_lugar_nacimiento: "",
    bautizado_genero: "masculino",
    padre_nombre: "",
    madre_nombre: "",
    padrino_nombre: "",
    madrina_nombre: "",
    ministro_nombre: "",
    notas_marginales: "",
  });

  useEffect(() => {
    if (isEditing) {
      loadBautizo();
    }
  }, [id]);

  const loadBautizo = async () => {
    try {
      const response = await bautizosAPI.getOne(id);
      setFormData(response.data);
    } catch (error) {
      toast.error("Error al cargar el bautizo");
      navigate("/bautizos");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditing) {
        await bautizosAPI.update(id, formData);
        toast.success("Bautizo actualizado correctamente");
      } else {
        await bautizosAPI.create(formData);
        toast.success("Bautizo registrado correctamente");
      }
      navigate("/bautizos");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el bautizo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="bautizo-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/bautizos")}
          className="h-10 w-10"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
            <Droplets className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
            {isEditing ? "Editar Bautizo" : "Nuevo Bautizo"}
          </h1>
          <p className="text-stone-600 mt-1">
            {isEditing ? "Modifica los datos del registro" : "Ingresa los datos del nuevo bautizo"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Datos del Registro */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Datos del Registro
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="numero_libro">Número de Libro *</Label>
              <Input
                id="numero_libro"
                name="numero_libro"
                value={formData.numero_libro}
                onChange={handleChange}
                required
                className="h-11"
                data-testid="numero-libro-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="numero_folio">Número de Folio *</Label>
              <Input
                id="numero_folio"
                name="numero_folio"
                value={formData.numero_folio}
                onChange={handleChange}
                required
                className="h-11"
                data-testid="numero-folio-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="numero_partida">Número de Partida *</Label>
              <Input
                id="numero_partida"
                name="numero_partida"
                value={formData.numero_partida}
                onChange={handleChange}
                required
                className="h-11"
                data-testid="numero-partida-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fecha_bautizo">Fecha del Bautizo *</Label>
              <Input
                id="fecha_bautizo"
                name="fecha_bautizo"
                type="date"
                value={formData.fecha_bautizo}
                onChange={handleChange}
                required
                className="h-11"
                data-testid="fecha-bautizo-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="lugar_bautizo">Lugar del Bautizo *</Label>
              <Input
                id="lugar_bautizo"
                name="lugar_bautizo"
                value={formData.lugar_bautizo}
                onChange={handleChange}
                required
                className="h-11"
                data-testid="lugar-bautizo-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Datos del Bautizado */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Datos del Bautizado
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="bautizado_nombre">Nombre *</Label>
              <Input
                id="bautizado_nombre"
                name="bautizado_nombre"
                value={formData.bautizado_nombre}
                onChange={handleChange}
                required
                className="h-11"
                data-testid="bautizado-nombre-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bautizado_apellido">Apellido *</Label>
              <Input
                id="bautizado_apellido"
                name="bautizado_apellido"
                value={formData.bautizado_apellido}
                onChange={handleChange}
                required
                className="h-11"
                data-testid="bautizado-apellido-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bautizado_fecha_nacimiento">Fecha de Nacimiento *</Label>
              <Input
                id="bautizado_fecha_nacimiento"
                name="bautizado_fecha_nacimiento"
                type="date"
                value={formData.bautizado_fecha_nacimiento}
                onChange={handleChange}
                required
                className="h-11"
                data-testid="bautizado-fecha-nacimiento-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bautizado_genero">Género *</Label>
              <Select
                value={formData.bautizado_genero}
                onValueChange={(value) => handleSelectChange("bautizado_genero", value)}
              >
                <SelectTrigger className="h-11" data-testid="bautizado-genero-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="masculino">Masculino</SelectItem>
                  <SelectItem value="femenino">Femenino</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="bautizado_lugar_nacimiento">Lugar de Nacimiento *</Label>
              <Input
                id="bautizado_lugar_nacimiento"
                name="bautizado_lugar_nacimiento"
                value={formData.bautizado_lugar_nacimiento}
                onChange={handleChange}
                required
                className="h-11"
                data-testid="bautizado-lugar-nacimiento-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Padres y Padrinos */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Padres y Padrinos
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="padre_nombre">Nombre del Padre</Label>
              <Input
                id="padre_nombre"
                name="padre_nombre"
                value={formData.padre_nombre}
                onChange={handleChange}
                className="h-11"
                data-testid="padre-nombre-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="madre_nombre">Nombre de la Madre</Label>
              <Input
                id="madre_nombre"
                name="madre_nombre"
                value={formData.madre_nombre}
                onChange={handleChange}
                className="h-11"
                data-testid="madre-nombre-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="padrino_nombre">Nombre del Padrino</Label>
              <Input
                id="padrino_nombre"
                name="padrino_nombre"
                value={formData.padrino_nombre}
                onChange={handleChange}
                className="h-11"
                data-testid="padrino-nombre-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="madrina_nombre">Nombre de la Madrina</Label>
              <Input
                id="madrina_nombre"
                name="madrina_nombre"
                value={formData.madrina_nombre}
                onChange={handleChange}
                className="h-11"
                data-testid="madrina-nombre-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Ministro y Notas */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Ministro y Observaciones
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="ministro_nombre">Nombre del Ministro *</Label>
              <Input
                id="ministro_nombre"
                name="ministro_nombre"
                value={formData.ministro_nombre}
                onChange={handleChange}
                required
                className="h-11"
                data-testid="ministro-nombre-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notas_marginales">Notas Marginales</Label>
              <Textarea
                id="notas_marginales"
                name="notas_marginales"
                value={formData.notas_marginales}
                onChange={handleChange}
                rows={4}
                className="resize-none"
                data-testid="notas-marginales-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/bautizos")}
            className="h-11"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="h-11 bg-rose-950 hover:bg-rose-900 text-white"
            data-testid="guardar-bautizo-btn"
          >
            {loading ? (
              <span className="animate-pulse">Guardando...</span>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? "Actualizar" : "Guardar"} Bautizo
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
